import { VStack } from "@/components/ui/vstack";
import { View } from "@/components/ui/view";
import { Text } from "@/components/ui/text";
import { Pressable } from "@/components/ui/pressable";
import { Menu, MenuItem, MenuItemLabel } from "@/components/ui/menu";
import { Image } from "@/components/ui/image";
import React, { useEffect, useState } from 'react';
import { switchScreen } from '@/hooks/navigate';
import { IconM } from '@/components/media/IconsV2';

import * as MediaSelector from '@/components/media/MediaSelector';
import i18n from "@/assets/i18n";
import Moment from "moment";
import * as assets_images from '@/assets/images';
import avatar1 from "@/assets/images/avatar_mit_background.png"

import { useNavigation } from "@react-navigation/native";

export function ContactCardHeader({ icon, subicon, callback }) {
  const on_press = () => {
    callback()
  };

  return (
    <Pressable onPress={on_press} className="shadow-hard-3 justify-center items-center m-2 w-[200px] h-[200px] bg-secondary-50 rounded-md border-primary-900 border-[2px]">
      <VStack className="relative">
        <IconM className='text-primary-900' size={60} name={icon.name} />
        {subicon && (
          <VStack className="absolute bg-secondary-50 top-[64%] left-[64%] rounded-[50%] items-center justify-center w-10 h-10 p-2">
            <IconM className='text-primary-900' size={36} name={subicon.name} />
          </VStack>
        )}
      </VStack>
    </Pressable>
  );
}

export function ContactCard({ entry }) {
  const navigation = useNavigation();

  const [card_title, setCardTitle] = useState();
  const [card_subtitle, setCardSubtitle] = useState();
  const [card_cover_media, setCardCoverMedia] = useState(MediaSelector.imageSrc(assets_images.placeholderImg, false));

  useEffect(() => {
    switch (true) {
      case 'workflow' in entry:
        setCardTitle(entry.workflow.title);
        setCardSubtitle(entry.workflow.description);
        if (entry.workflow.workflowImage.id) {
          setCardCoverMedia(MediaSelector.imageSrc(entry.workflow.workflowImage.id, false));
        }
        break;
      case 'user' in entry:
        setCardTitle(entry.user.preferredName);
        setCardSubtitle(entry.contactAlias ?? entry.user.mail);
        if (entry.user.userImage.id) {
          setCardCoverMedia(MediaSelector.imageSrc(entry?.contactImage?.id ?? entry.user.userImage.id, false));
        }
        break;
      case 'fromUser' in entry:
      case 'toUser' in entry:
        const entry_user = entry.fromUser ?? entry.toUser;
        setCardTitle(entry_user.mail);
        setCardSubtitle(i18n.t('contactManagement.request', { date: Moment(entry.createdAt).format('DD.MM.YYYY') }));
        setCardCoverMedia(avatar1); // ? always use placeholder for unkowen (non-active) user
        break;
      case 'channel' in entry:
        setCardTitle(entry.channel.name);
        setCardSubtitle(entry.channel.description);
        if (entry.channel.channelImage.id) {
          setCardCoverMedia(MediaSelector.imageSrc(entry.channel.channelImage.id, false));
        }
        break;
    }
  }, [entry]);

  const on_press = () => {
    switch (true) {
      case 'workflow' in entry:
        switchScreen(navigation, 'editor', { k: entry.workflow.id })
        break;
      case 'user' in entry:
        switchScreen(navigation, 'ViewContact', { k: entry.user.id })
        break;
      case 'channel' in entry:
        switchScreen(navigation, 'ViewChannel', { k: entry.channel.id })
        break;
    }
  }

  return (
    <Pressable onPress={on_press} disabled={'workflow' in entry} className="data-[disabled=true]:opacity-100 m-2">
      <VStack className="shadow-hard-3 w-[200px] h-[200px] rounded-md border border-[#BEBEBE] bg-secondary-50 overflow-hidden">
        <VStack className="w-[200px] h-[200px] overflow-hidden justify-end items-stretch">

          {/* cover */}
          <CardMediaBox media={card_cover_media} />

          {/* title and subtitle */}
          <VStack space='xs' className="bg-secondary-50 p-2 min-h-[64px] justify-center items-center">
            {!!card_title && <Text isTruncated={true} className="max-w-full text-md font-semibold text-secondary-900">{card_title}</Text> }
            {!!card_subtitle && <Text isTruncated={true} className="max-w-full text-md text-secondary-900">{card_subtitle}</Text> }
          </VStack>

          {/* context */}
          {!!entry.context && <CardContext entry={entry} />}

        </VStack>
      </VStack>
    </Pressable>
  );
}

const CardContext = ({ entry }) => {
  const render_context_trigger = (context_trigger_properties) => {
    return (
      <View className="absolute p-1 top-1 right-1 bg-[#FFFFFFC0] border border-primary-900 rounded-[4px]">
        <IconM className='text-primary-900' size={24} name='more-vert' />
        <Pressable {...context_trigger_properties} className="absolute w-8 h-8" />
      </View>
    );
  };

  const render_context_entry = (context_entry, index) => {
    const on_context_press = () => {
      // TODO(@Milo): close context menu
      context_entry.callback({ entry });
    };

    return (
      <MenuItem
        key={index} // FIXME(@Milo): Add unique key
        onPress={on_context_press}
        textValue={context_entry.name}
      >
        <MenuItemLabel className={`text-sm ${context_entry.action === 'negative' ? 'text-error-600' : ''}`}>{context_entry.name}</MenuItemLabel>
      </MenuItem>
    );
  };

  return (
    <Menu placement='bottom left' trigger={render_context_trigger}>
      {entry.context.map(render_context_entry)}
    </Menu>
  );
};

const CardMediaBox = ({ media }) => {
  return (
    <Image
      key={media} source={media} resizeMode='cover'
      fallbackSource={assets_images.placeholderImg} alt={i18n.t('aria.image.card')}
      className="flex-1 w-full"
    />
  );
}
